import {
  Allergen,
  CatBrandBefore,
  CatFoodTypeBefore,
  CatLifeStage,
  CatMedicalCondition,
} from "@/primary-models";

import {
  AdultWeightProfile,
  AgeType,
  BinaryGender,
  BodyType,
  BodyTypeCalorieMultiplier,
  Breed,
  IAllergy,
  IBodyType,
  IBreed,
  IMedicalCondition,
  LifeStyle,
  LifeStyleCalorieMultiplier,
  MealPlanCalories,
  PouchSize,
  RetailTrayPortions,
} from "./cat-calculations.types";

export const WEEKS_PER_MONTH = 4.33;

// Meal plan intervals are 50kcal and plans are matched on `calories <= plan + threshold`,
// meaning a threshold of 35 gives the +15/-35 rounding desired.
export const CALORIE_THRESHOLD = 35;

// Kittens keep the old symmetric ±25 rounding.
export const KITTEN_CALORIE_THRESHOLD = 25;

export const KITTEN_AGE_MONTHS: number[] = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11,
];
export const KITTEN_AGE_WEEKS: number[] = [ 0, 1, 2, 3, 4 ];

export const ADULT_CAT_AGE_YEARS: number[] = [ 1, 2, 3, 4, 5, 6, 7, 8 ];
export const SENIOR_CAT_AGE_YEARS: number[] = [
  9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
];

export const AGE_TYPE: Record<AgeType, string> = {
  kitten: "Kitten (Under 1 year old)",
  adult: "Adult (1-8 years old)",
  senior: "Senior (Over 8 years old)",
};

export const NEUTERED_OR_SPAYED: Record<string, string> = {
  YES: "Yes",
  NO: "No",
} as const;

export const BINARY_GENDER: Record<BinaryGender, string> = {
  BOY: "Boy",
  GIRL: "Girl",
} as const;

export const LIFESTYLE: Partial<Record<LifeStyle, string>> = {
  INDOOR_LOW_ACTIVITY: "Only indoor, low activity",
  INDOOR_HIGH_ACTIVITY: "Only indoor, high activity",
  INDOOR_AND_OUTDOOR: "Indoor and outdoor",
  MOSTLY_OUTDOOR: "Mostly outdoor",
} as const;

export const BODY_TYPE: Partial<Record<BodyType, IBodyType>> = {
  VERY_THIN: {
    name: "Very thin",
    icon: "catBodyVeryThin",
  },
  THIN: {
    name: "Thin",
    icon: "catBodyThin",
  },
  JUST_RIGHT: {
    name: "Just right",
    icon: "catBodyJustRight",
  },
  OVERWEIGHT: {
    name: "Overweight",
    icon: "catBodyOverweight",
  },
  VERY_OVERWEIGHT: {
    name: "Very overweight",
    icon: "catBodyVeryOverweight",
  },
};

export const MAX_WEIGHT_ALLOWED_KG = 20;
export const MAX_WEIGHT_ALLOWED_LB = 44;
export const KG_TO_LB = 2.20462;

export const GROWTH_CURVE: number[] = [
  0, 6.9, 12.1, 17.2, 24.1, 32.8, 39.7, 48.3, 55.2, 60.3, 65.5, 70.7, 75.9,
  79.3, 82.8, 86.2, 89.7, 91.4, 93.1, 94.8, 96.6, 97.4, 98.3, 99.1, 100.0,
  100.0, 100.0,
];

export const BODY_TYPE_CALORIE_MULTIPLIER: BodyTypeCalorieMultiplier = {
  VERY_THIN: 1.42,
  THIN: 1.1,
  JUST_RIGHT: 1,
  OVERWEIGHT: 0.8,
  VERY_OVERWEIGHT: 0.67,
  UNKNOWN: 1,
};

export const LIFESTYLE_CALORIE_MULTIPLIER: LifeStyleCalorieMultiplier = {
  INDOOR_LOW_ACTIVITY: 0.8,
  INDOOR_HIGH_ACTIVITY: 0.9,
  INDOOR_AND_OUTDOOR: 1,
  MOSTLY_OUTDOOR: 1.1,
  UNKNOWN: 1,
};

/** Intact cats need more energy than neutered ones for the same ideal body weight. */
export const NEUTERED_CALORIE_MULTIPLIER = {
  NEUTERED: 1,
  INTACT: 1.15,
} as const;

export const CALORIES_PER_KG_PER_DAY = 75;

/** Energy needs scale with metabolic body weight rather than body weight itself. */
export const METABOLIC_WEIGHT_EXPONENT = 0.67;

/**
 * Kitten energy requirements come from two formulas that meet at 14 weeks.
 * Up to 14 weeks: growth equation compares the kitten's current weight against their target adult weight.
 * From 15 weeks: multiple of maintenance for target adult weight, tapering as they approach 1 year.
 */
export const KITTEN_GROWTH_COEFFICIENT = 6.7;
export const KITTEN_MATURITY_RATIO_COEFFICIENT = -0.189;
export const KITTEN_GROWTH_EQUATION_OFFSET = 0.66;
export const KITTEN_MAINTENANCE_CALORIES_PER_KG = 50;

export const MEAL_PLAN_CALORIES: MealPlanCalories = {
  A: 150,
  B: 200,
  C: 250,
  D: 300,
  E: 350,
  F: 400,
};

export const RETAIL_PLAN_PORTION: RetailTrayPortions = {
  150: 0.75,
  200: 1,
  250: 1.25,
  300: 1.5,
  350: 1.75,
  400: 2,
};

/**
* Weight profiles — shared across breeds, all in kg.
* Named by JUST_RIGHT estimated weight, e.g. WEIGHT_PROFILE_4_5 is used for a JUST_RIGHT cat of 4.5kg.
* If a new breed is added, it should be assigned to the appropriate existing weight profile.
*/
const WEIGHT_PROFILE_3_0: AdultWeightProfile = {
  VERY_THIN:       { lower: 2, estimated: 2.1, upper: 3 },
  THIN:            { lower: 2.5, estimated: 2.7, upper: 3.5 },
  JUST_RIGHT:      { lower: 2.5, estimated: 3, upper: 3.5 },
  OVERWEIGHT:      { lower: 3.5, estimated: 3.8, upper: 4.5 },
  VERY_OVERWEIGHT: { lower: 4, estimated: 4.5, upper: 5 },
  UNKNOWN:         { lower: 2.5, estimated: 3, upper: 3.5 },
};

const WEIGHT_PROFILE_3_5: AdultWeightProfile = {
  VERY_THIN:       { lower: 2, estimated: 2.5, upper: 3 },
  THIN:            { lower: 3, estimated: 3.2, upper: 4 },
  JUST_RIGHT:      { lower: 3, estimated: 3.5, upper: 4 },
  OVERWEIGHT:      { lower: 4, estimated: 4.4, upper: 5 },
  VERY_OVERWEIGHT: { lower: 5, estimated: 5.2, upper: 6 },
  UNKNOWN:         { lower: 3, estimated: 3.5, upper: 4 },
};

const WEIGHT_PROFILE_4_0: AdultWeightProfile = {
  VERY_THIN:       { lower: 2.5, estimated: 2.8, upper: 3.5 },
  THIN:            { lower: 3.5, estimated: 3.6, upper: 4.5 },
  JUST_RIGHT:      { lower: 3.5, estimated: 4, upper: 4.5 },
  OVERWEIGHT:      { lower: 4.5, estimated: 5.0, upper: 5.5 },
  VERY_OVERWEIGHT: { lower: 5.5, estimated: 6.0, upper: 6.5 },
  UNKNOWN:         { lower: 3.5, estimated: 4, upper: 4.5 },
};

const WEIGHT_PROFILE_4_5: AdultWeightProfile = {
  VERY_THIN:       { lower: 3, estimated: 3.2, upper: 4 },
  THIN:            { lower: 4, estimated: 4.1, upper: 5 },
  JUST_RIGHT:      { lower: 4, estimated: 4.5, upper: 5 },
  OVERWEIGHT:      { lower: 5.5, estimated: 5.6, upper: 6.5 },
  VERY_OVERWEIGHT: { lower: 6.5, estimated: 6.7, upper: 7.5 },
  UNKNOWN:         { lower: 4, estimated: 4.5, upper: 5 },
};

const WEIGHT_PROFILE_5_0: AdultWeightProfile = {
  VERY_THIN:       { lower: 3, estimated: 3.5, upper: 4 },
  THIN:            { lower: 4, estimated: 4.5, upper: 5 },
  JUST_RIGHT:      { lower: 4.5, estimated: 5, upper: 5.5 },
  OVERWEIGHT:      { lower: 6, estimated: 6.3, upper: 7 },
  VERY_OVERWEIGHT: { lower: 7, estimated: 7.5, upper: 8 },
  UNKNOWN:         { lower: 4.5, estimated: 5, upper: 5.5 },
};

const WEIGHT_PROFILE_5_5: AdultWeightProfile = {
  VERY_THIN:       { lower: 3.5, estimated: 3.9, upper: 4.5 },
  THIN:            { lower: 4.5, estimated: 5, upper: 5.5 },
  JUST_RIGHT:      { lower: 5, estimated: 5.5, upper: 6 },
  OVERWEIGHT:      { lower: 6.5, estimated: 6.9, upper: 7.5 },
  VERY_OVERWEIGHT: { lower: 8, estimated: 8.2, upper: 9 },
  UNKNOWN:         { lower: 5, estimated: 5.5, upper: 6 },
};

const WEIGHT_PROFILE_6_0: AdultWeightProfile = {
  VERY_THIN:       { lower: 4, estimated: 4.2, upper: 5 },
  THIN:            { lower: 5, estimated: 5.5, upper: 6 },
  JUST_RIGHT:      { lower: 5.5, estimated: 6, upper: 6.5 },
  OVERWEIGHT:      { lower: 7, estimated: 7.5, upper: 8 },
  VERY_OVERWEIGHT: { lower: 8.5, estimated: 9.0, upper: 9.5 },
  UNKNOWN:         { lower: 5.5, estimated: 6, upper: 6.5 },
};

const WEIGHT_PROFILE_6_5: AdultWeightProfile = {
  VERY_THIN:       { lower: 4.5, estimated: 4.6, upper: 5.5 },
  THIN:            { lower: 5.5, estimated: 5.9, upper: 6.5 },
  JUST_RIGHT:      { lower: 6, estimated: 6.5, upper: 7 },
  OVERWEIGHT:      { lower: 8, estimated: 8.1, upper: 9 },
  VERY_OVERWEIGHT: { lower: 9.5, estimated: 9.7, upper: 10.5 },
  UNKNOWN:         { lower: 6, estimated: 6.5, upper: 7 },
};

const WEIGHT_PROFILE_7_0: AdultWeightProfile = {
  VERY_THIN:       { lower: 4.5, estimated: 4.9, upper: 5.5 },
  THIN:            { lower: 6, estimated: 6.4, upper: 7 },
  JUST_RIGHT:      { lower: 6.5, estimated: 7, upper: 7.5 },
  OVERWEIGHT:      { lower: 8.5, estimated: 8.8, upper: 9.5 },
  VERY_OVERWEIGHT: { lower: 10, estimated: 10.4, upper: 11 },
  UNKNOWN:         { lower: 6.5, estimated: 7, upper: 7.5 },
};

export const BREEDS: IBreed[] = [
  {
    value: "DOMESTIC_SHORT_HAIR",
    text: "Domestic Short-hair",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: true,
    isPedigree: false,
    isLongHaired: false,
  },
  {
    value: "DOMESTIC_MEDIUM_HAIR",
    text: "Domestic Medium-hair",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_3_5,
    isMoggie: true,
    isPedigree: false,
    isLongHaired: true,
  },
  {
    value: "DOMESTIC_LONG_HAIR",
    text: "Domestic Long-hair",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_3_5,
    isMoggie: true,
    isPedigree: false,
    isLongHaired: true,
  },
  {
    value: "ABYSSINIAN",
    text: "Abyssinian",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_3_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "AMERICAN_BOBTAIL",
    text: "American Bobtail",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_5_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "AMERICAN_CURL",
    text: "American Curl",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_3_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "AMERICAN_SHORTHAIR",
    text: "American Short-hair",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_5_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "BALINESE",
    text: "Balinese",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "BENGAL",
    text: "Bengal",
    maleWeights: WEIGHT_PROFILE_5_5,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "BIRMAN",
    text: "Birman",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_5_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "BRITISH_SHORTHAIR",
    text: "British Short-hair",
    maleWeights: WEIGHT_PROFILE_5_5,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "BRITISH_LONGHAIR",
    text: "British Long-hair",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "BURMESE",
    text: "Burmese",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_5_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "BURMILLA",
    text: "Burmilla",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "CHAUSIE",
    text: "Chausie",
    maleWeights: WEIGHT_PROFILE_6_0,
    femaleWeights: WEIGHT_PROFILE_6_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "CHINCHILLA",
    text: "Chinchilla",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "CORNISH_REX",
    text: "Cornish Rex",
    maleWeights: WEIGHT_PROFILE_3_0,
    femaleWeights: WEIGHT_PROFILE_3_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "DEVON_REX",
    text: "Devon Rex",
    maleWeights: WEIGHT_PROFILE_3_0,
    femaleWeights: WEIGHT_PROFILE_3_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "EXOTIC_SHORTHAIR",
    text: "Exotic Short-hair",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "JAPANESE_BOBTAIL",
    text: "Japanese Bobtail",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_3_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "KORAT",
    text: "Korat",
    maleWeights: WEIGHT_PROFILE_3_5,
    femaleWeights: WEIGHT_PROFILE_3_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "KURILIAN_BOBTAILS",
    text: "Kurilian Bobtail",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "LAPERM",
    text: "LaPerm",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_3_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "MAINE_COON",
    text: "Maine Coon",
    maleWeights: WEIGHT_PROFILE_7_0,
    femaleWeights: WEIGHT_PROFILE_6_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "MANX",
    text: "Manx",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "MOGGIE",
    text: "Moggie",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: true,
    isPedigree: false,
    isLongHaired: false,
  },
  {
    value: "NORWEGIAN_FOREST_CAT",
    text: "Norwegian Forest Cat",
    maleWeights: WEIGHT_PROFILE_6_5,
    femaleWeights: WEIGHT_PROFILE_6_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "OCICAT",
    text: "Ocicat",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "ORIENTAL_SHORTHAIR",
    text: "Oriental Short-hair",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_3_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "PERSIAN",
    text: "Persian",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "PETERBALD",
    text: "Peterbald",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "PIXIE_BOB",
    text: "Pixie Bob",
    maleWeights: WEIGHT_PROFILE_5_5,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "RAGAMUFFIN",
    text: "Ragamuffin",
    maleWeights: WEIGHT_PROFILE_7_0,
    femaleWeights: WEIGHT_PROFILE_6_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "RAGDOLL",
    text: "Ragdoll",
    maleWeights: WEIGHT_PROFILE_6_0,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "RUSSIAN_BLUE",
    text: "Russian Blue",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "SAVANNAH",
    text: "Savannah",
    maleWeights: WEIGHT_PROFILE_7_0,
    femaleWeights: WEIGHT_PROFILE_7_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "SCOTTISH_FOLD",
    text: "Scottish Fold",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "SIAMESE",
    text: "Siamese",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_3_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "SIBERIAN_FOREST_CAT",
    text: "Siberian Forest Cat",
    maleWeights: WEIGHT_PROFILE_7_0,
    femaleWeights: WEIGHT_PROFILE_6_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "SINGAPURA",
    text: "Singapura",
    maleWeights: WEIGHT_PROFILE_3_0,
    femaleWeights: WEIGHT_PROFILE_3_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "SNOWSHOE",
    text: "Snowshoe",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "SOMALI",
    text: "Somali",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "SPHYNX",
    text: "Sphynx",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "TIFFANIE",
    text: "Tiffanie",
    maleWeights: WEIGHT_PROFILE_4_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "TONKINESE",
    text: "Tonkinese",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: false,
  },
  {
    value: "TURKISH_ANGORA",
    text: "Turkish Angora",
    maleWeights: WEIGHT_PROFILE_5_0,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "TURKISH_VAN",
    text: "Turkish Van",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_5,
    isMoggie: false,
    isPedigree: true,
    isLongHaired: true,
  },
  {
    value: "OTHER",
    text: "Other",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: true,
    isPedigree: false,
    isLongHaired: false,
  },
  {
    value: "I_DONT_KNOW_THEIR_BREED",
    text: "I don't know their breed",
    maleWeights: WEIGHT_PROFILE_4_5,
    femaleWeights: WEIGHT_PROFILE_4_0,
    isMoggie: true,
    isPedigree: false,
    isLongHaired: false,
  },
];

export const BREED_MAP: Map<Breed, string> = new Map(
  BREEDS.map((b) => [ b.value, b.text ]),
);

export const CAT_LEGACY_FOOD_TYPE_TO_FOOD_TYPE: Record<TFoodType, CatFoodTypeBefore> = {
  Wet: CatFoodTypeBefore.WET,
  Dry: CatFoodTypeBefore.DRY,
  "Dry,Wet": CatFoodTypeBefore.WET,
  "Home-cooked": CatFoodTypeBefore.HOME_COOKED,
  Raw: CatFoodTypeBefore.RAW,
  Fresh: CatFoodTypeBefore.FRESH,
} as const;

export const CAT_FOOD_TYPE = {
  Wet: "Wet",
  Dry: "Dry",
  "Dry,Wet": "Wet & Dry",
  "Home-cooked": "Home-cooked",
  Raw: "Raw",
  Fresh: "Fresh",
} as const;

export const CAT_FOOD_BRAND = {
  FELIX: "Felix",
  WHISKAS: "Whiskas",
  GOURMET: "Gourmet",
  SHEBA: "Sheba",
  ROYAL_CANIN: "Royal Canin",
  PURINA_ONE: "Purina One",
  APPLAWS: "Applaws",
  AARDVARK: "Aardvark",
  ALMO_NATURE: "Almo Nature",
  AVA: "Ava",
  BLINK: "Blink",
  BURNS: "Burns",
  CARNILOVE: "Carnilove",
  CRAVE: "Crave",
  EDGARD_COOPER: "Edgard Cooper",
  ENCORE: "Encore",
  FERINGA: "Fergina",
  FRESHPET: "Freshpet",
  GO_CAT: "Go Cat",
  HARRINGTONS: "Harringtons",
  HILLS: "Hills",
  IAMS: "Iams",
  JAMES_WELLBELOVED: "James Wellbeloved",
  KATKIN: "Katkin",
  LILYS_KITCHEN: "Lily's Kitchen",
  MEOWING_HEADS: "Meowing Heads",
  NATURAL_INSTINCT: "Natural Instinct",
  NATURES_MENU: "Natures Menu",
  NUTRIMENT: "Nutriment",
  ORIJEN: "Origen",
  "PURR&MIAOW": "Purr & Meow",
  PURRFORM: "Purrform",
  REPUBLIC_OF_CATS: "Republic of Cats",
  SCRUMBLES: "Scrumbles",
  SUPERMARKET_BRAND: "Supermarket brand",
  THRIVE: "Thrive",
  TOMOJO: "Tomojo",
  UNTAMED: "Untamed",
  MARRO: "Marro",
  OTHER: "Other",
} as const;

export const CAT_FOOD_BRAND_FILTERED = Object.fromEntries(
  Object.entries(CAT_FOOD_BRAND).filter(([ key ]) => ![ "PURR&MIAOW" ].includes(key)),
) as typeof CAT_FOOD_BRAND;

export const CAT_FOOD_BRAND_BEFORE: Record<CatBrandBefore, string> = {
  ...CAT_FOOD_BRAND_FILTERED,
  [CatBrandBefore.PURR_AND_MIAOW]: "Purr & Meow",
  [CatBrandBefore.UNKNOWN]: "Unknown",
} as const;

export const CAT_ACTIVITY_LEVEL = {
  INDOOR_LOW_ACTIVITY: "Only indoor, low activity",
  INDOOR_HIGH_ACTIVITY: "Only indoor, high activity",
  INDOOR_AND_OUTDOOR: "Indoor and outdoor",
  MOSTLY_OUTDOOR: "Mostly outdoor",
  UNKNOWN: "Unknown",
} as const;

export const CAT_BODY_TYPE = {
  VERY_THIN: "Very thin",
  THIN: "Thin",
  JUST_RIGHT: "Just right",
  OVERWEIGHT: "Overweight",
  VERY_OVERWEIGHT: "Very overweight",
  UNKNOWN: "Unknown",
} as const;

export const CAT_DESIRED_BENEFITS = {
  "lose-weight": "Lose weight",
  "gain-weight": "Gain weight",
  "sensitive-tummy": "Less sensitive tummy",
  "healthier-coat": "Healthier coat",
  "smelly-poo": "Less smelly poos",
  "more-energy": "More energy",
  "immune-system": "Improved immune system",
} as const;

export const CAT_FUSSINESS = {
  NOT_FUSSY: "Not fussy",
  QUITE_FUSSY: "Quite fussy",
  VERY_FUSSY: "Very fussy",
} as const;

export type TFoodType = keyof typeof CAT_FOOD_TYPE;
export type TBodyType = keyof typeof CAT_BODY_TYPE;
export type TLifeStyle = keyof typeof CAT_ACTIVITY_LEVEL;
export type TFoodBrand = keyof typeof CAT_FOOD_BRAND;
export type TDesiredBenefit = keyof typeof CAT_DESIRED_BENEFITS;
export type TFussiness = keyof typeof CAT_FUSSINESS;

export interface ICompetitorFood {
  costPerDay: number;
  meatPercentage: number;
}

type TCompetitorPortion = Record<PouchSize, ICompetitorFood>;
type TCompetitorType = Partial<Record<TFoodType, TCompetitorPortion>>;

type TCompetitorMap = Partial<Record<TFoodBrand, TCompetitorType>>;

export const COMPETITOR_MAP_LAST_UPDATED = "19/08/2024";
export const competitorMap: TCompetitorMap = {
  WHISKAS: {
    Dry: {
      A: {
        costPerDay: 0.11,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 0.15,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 0.18,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 0.22,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 0.26,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 0.29,
        meatPercentage: 4,
      },
    },
    "Dry,Wet": {
      A: {
        costPerDay: 0.53,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 0.71,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 0.89,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 1.06,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 1.24,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 1.42,
        meatPercentage: 4,
      },
    },
    Wet: {
      A: {
        costPerDay: 0.95,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 1.27,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 1.59,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 1.91,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 2.22,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 2.54,
        meatPercentage: 4,
      },
    },
  },
  FELIX: {
    "Dry,Wet": {
      A: {
        costPerDay: 0.38,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 0.61,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 0.76,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 0.91,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 1.07,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 1.22,
        meatPercentage: 4,
      },
    },
    Wet: {
      A: {
        costPerDay: 0.75,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 1.0,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 1.25,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 1.5,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 1.75,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 2.0,
        meatPercentage: 4,
      },
    },
  },
  SHEBA: {
    "Dry,Wet": {
      A: {
        costPerDay: 1.27,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 1.69,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 2.12,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 2.54,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 2.97,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 3.39,
        meatPercentage: 4,
      },
    },
    Wet: {
      A: {
        costPerDay: 1.27,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 1.69,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 2.12,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 2.54,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 2.97,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 3.39,
        meatPercentage: 4,
      },
    },
  },
  PURINA_ONE: {
    Dry: {
      A: {
        costPerDay: 0.36,
        meatPercentage: 17,
      },
      B: {
        costPerDay: 0.47,
        meatPercentage: 17,
      },
      C: {
        costPerDay: 0.59,
        meatPercentage: 17,
      },
      D: {
        costPerDay: 0.71,
        meatPercentage: 17,
      },
      E: {
        costPerDay: 0.83,
        meatPercentage: 17,
      },
      F: {
        costPerDay: 0.95,
        meatPercentage: 17,
      },
    },
    "Dry,Wet": {
      A: {
        costPerDay: 0.57,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 0.76,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 0.95,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 1.14,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 1.33,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 1.53,
        meatPercentage: 4,
      },
    },
  },
  GOURMET: {
    "Dry,Wet": {
      A: {
        costPerDay: 0.94,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 1.26,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 1.57,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 1.89,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 2.2,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 2.51,
        meatPercentage: 4,
      },
    },
    Wet: {
      A: {
        costPerDay: 0.94,
        meatPercentage: 4,
      },
      B: {
        costPerDay: 1.26,
        meatPercentage: 4,
      },
      C: {
        costPerDay: 1.57,
        meatPercentage: 4,
      },
      D: {
        costPerDay: 1.89,
        meatPercentage: 4,
      },
      E: {
        costPerDay: 2.2,
        meatPercentage: 4,
      },
      F: {
        costPerDay: 2.51,
        meatPercentage: 4,
      },
    },
  },
  ROYAL_CANIN: {
    "Dry,Wet": {
      A: {
        costPerDay: 1.71,
        meatPercentage: 0,
      },
      B: {
        costPerDay: 2.28,
        meatPercentage: 0,
      },
      C: {
        costPerDay: 2.85,
        meatPercentage: 0,
      },
      D: {
        costPerDay: 3.42,
        meatPercentage: 0,
      },
      E: {
        costPerDay: 3.99,
        meatPercentage: 0,
      },
      F: {
        costPerDay: 4.56,
        meatPercentage: 0,
      },
    },
    Dry: {
      A: {
        costPerDay: 0.43,
        meatPercentage: 0,
      },
      B: {
        costPerDay: 0.58,
        meatPercentage: 0,
      },
      C: {
        costPerDay: 0.72,
        meatPercentage: 0,
      },
      D: {
        costPerDay: 0.87,
        meatPercentage: 0,
      },
      E: {
        costPerDay: 1.01,
        meatPercentage: 0,
      },
      F: {
        costPerDay: 1.16,
        meatPercentage: 0,
      },
    },
  },
  SUPERMARKET_BRAND: {
    "Dry,Wet": {
      A: {
        costPerDay: 0.21,
        meatPercentage: 38,
      },
      B: {
        costPerDay: 0.28,
        meatPercentage: 38,
      },
      C: {
        costPerDay: 0.35,
        meatPercentage: 38,
      },
      D: {
        costPerDay: 0.43,
        meatPercentage: 38,
      },
      E: {
        costPerDay: 0.5,
        meatPercentage: 38,
      },
      F: {
        costPerDay: 0.57,
        meatPercentage: 38,
      },
    },
  },
  APPLAWS: {
    "Dry,Wet": {
      A: {
        costPerDay: 1.32,
        meatPercentage: 75,
      },
      B: {
        costPerDay: 1.76,
        meatPercentage: 75,
      },
      C: {
        costPerDay: 1.76,
        meatPercentage: 75,
      },
      D: {
        costPerDay: 2.21,
        meatPercentage: 75,
      },
      E: {
        costPerDay: 2.65,
        meatPercentage: 75,
      },
      F: {
        costPerDay: 3.09,
        meatPercentage: 75,
      },
    },
  },
  IAMS: {
    "Dry,Wet": {
      A: {
        costPerDay: 1.05,
        meatPercentage: 26,
      },
      B: {
        costPerDay: 1.4,
        meatPercentage: 26,
      },
      C: {
        costPerDay: 1.75,
        meatPercentage: 26,
      },
      D: {
        costPerDay: 2.21,
        meatPercentage: 26,
      },
      E: {
        costPerDay: 2.65,
        meatPercentage: 26,
      },
      F: {
        costPerDay: 3.09,
        meatPercentage: 26,
      },
    },
  },
  UNTAMED: {
    Wet: {
      A: {
        costPerDay: 2.14,
        meatPercentage: 60,
      },
      B: {
        costPerDay: 2.85,
        meatPercentage: 60,
      },
      C: {
        costPerDay: 3.57,
        meatPercentage: 60,
      },
      D: {
        costPerDay: 4.28,
        meatPercentage: 60,
      },
      E: {
        costPerDay: 4.99,
        meatPercentage: 60,
      },
      F: {
        costPerDay: 5.7,
        meatPercentage: 60,
      },
    },
  },
};

export const ALLERGEN = {
  CHICKEN: "CHICKEN",
  DUCK: "DUCK",
  TURKEY: "TURKEY",
  BEEF: "BEEF",
  LAMB: "LAMB",
  PORK: "PORK",
  SALMON: "SALMON",
  TUNA: "TUNA",
  WHITEFISH: "WHITEFISH",
} as const;

export const UNIVERSAL_ALLERGENS: Allergen[][] = [
  [ Allergen.CHICKEN, Allergen.SALMON ],
  [ Allergen.CHICKEN, Allergen.TURKEY, Allergen.BEEF, Allergen.PORK ],
];
export const ALLERGIES: IAllergy[] = [
  {
    value: Allergen.CHICKEN,
    title: "Chicken",
    aliases: [ "chicken", "poultry" ],
  },
  {
    value: Allergen.TURKEY,
    title: "Turkey",
    aliases: [ "turkey" ],
  },
  {
    value: Allergen.BEEF,
    title: "Beef",
    aliases: [ "beef" ],
  },
  {
    value: Allergen.LAMB,
    title: "Lamb",
    aliases: [ "lamb" ],
  },
  {
    value: Allergen.DUCK,
    title: "Duck",
    aliases: [ "duck" ],
  },
  {
    value: Allergen.WHITEFISH,
    title: "Whitefish",
    aliases: [ "whitefish", "fish" ],
  },
  {
    value: Allergen.SALMON,
    title: "Salmon",
    aliases: [ "salmon", "fish" ],
  },
  {
    value: Allergen.TUNA,
    title: "Tuna",
    aliases: [ "tuna", "fish" ],
  },
  {
    value: Allergen.PORK,
    title: "Pork",
    aliases: [ "pork", "swine" ],
  },
];

export const MEDICAL_CONDITIONS_NAME = CatMedicalCondition;

export const MEDICAL_CONDITIONS: IMedicalCondition[] = [
  {
    value: MEDICAL_CONDITIONS_NAME.A_FOOD_ALLERGY,
    title: "Allergies",
    aliases: [ "allergy" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.INFLAMMATORY_BOWEL_DISEASE,
    title: "Inflammatory bowel disease",
    aliases: [ "IBD" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.DIABETES,
    title: "Diabetes",
    aliases: [ "diabetes" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.KIDNEY_DISEASE,
    title: "Kidney disease",
    aliases: [ "kidney disease", "kidney failure" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.URINARY_DISEASE,
    title: "Urinary disease",
    aliases: [ "urinary disease", "UTI" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.DIARRHOEA_OF_AN_UNKNOWN_CAUSE,
    title: "Diarrhoea of an unknown cause",
    aliases: [ "diarrhoea", "diarrhea", "diarrhea of an unknown cause" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.VOMITING_OF_AN_UNKNOWN_CAUSE,
    title: "Vomiting of an unknown cause",
    aliases: [ "vomiting", "vomiting of an unknown cause" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.HYPERTHYROIDISM,
    title: "Hyperthyroidism",
    aliases: [ "hyperthyroidism" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.PANCREATITIS,
    title: "Pancreatitis",
    aliases: [ "pancreatitis" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.CYSTITIS,
    title: "Cystitis",
    aliases: [ "cystitis" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.ARTHRITIS,
    title: "Arthritis",
    aliases: [ "arthritis" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.LIVER_DISEASE,
    title: "Liver disease",
    aliases: [ "liver disease" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.FELINE_ASTHMAS,
    title: "Feline asthma",
    aliases: [ "feline asthma" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.EPILEPSY,
    title: "Epilepsy",
    aliases: [ "epilepsy" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.CANCER,
    title: "Cancer",
    aliases: [ "cancer" ],
  },
  {
    value: MEDICAL_CONDITIONS_NAME.OTHER,
    title: "Other",
    aliases: [ "other", "other medical condition" ],
  },
];

export const MEDICAL_VALUES_SET = new Set(
  MEDICAL_CONDITIONS.map((c) => c.value),
);

export const MEDICAL_CONDITION_WARNING_MESSAGES: Partial<Record<CatMedicalCondition, {
  icon: "success" | "error",
  body: string;
}>> = {
  [MEDICAL_CONDITIONS_NAME.INFLAMMATORY_BOWEL_DISEASE]: {
    icon: "success",
    body: "We know IBD can be tricky to manage, so we recommend chatting to your vet before switching to a new diet. However, highly digestible diets, like KatKin, are often recommended for IBD and other chronic digestive disorders in cats.",
  },
  [MEDICAL_CONDITIONS_NAME.DIABETES]: {
    icon: "success",
    body: "High protein, low carbohydrate diets like KatKin are commonly recommended for cats with diabetes. Cats sometimes require less insulin as a result. But don’t make any changes to your cat’s diet or insulin dose before speaking to your vet. They will be able to give you the best guidance on your cat’s specific needs.",
  },
  [MEDICAL_CONDITIONS_NAME.KIDNEY_DISEASE]: {
    icon: "error",
    body: "At the moment we don't have any specialised renal diets. Cats with CKD sometimes require a specialised diet, so chat to your vet about whether a maintenance diet like KatKin would be appropriate for your cat. We hope to have kidney-friendly diets in the future.",
  },
  [MEDICAL_CONDITIONS_NAME.URINARY_DISEASE]: {
    icon: "error",
    body: "The high water content of KatKin can be helpful for urinary disease. If your cat suffers from urinary stones or urinary crystals, make sure to speak to your vet before switching diets.",
  },
  [MEDICAL_CONDITIONS_NAME.DIARRHOEA_OF_AN_UNKNOWN_CAUSE]: {
    icon: "success",
    body: "Highly digestible diets are commonly recommended for chronic digestive disorders in cats, so our KatKin meals could be ideal. Our recipes use only fresh human-quality ingredients and 100% real meat, which makes our meals easy to digest. Be sure to ask your vet first about your cat’s specific needs.",
  },
  [MEDICAL_CONDITIONS_NAME.HYPERTHYROIDISM]: {
    icon: "success",
    body: "If your cat's hyperthyroidism is being controlled through medical management, our fresh meals would be suitable for your cat.",
  },
  [MEDICAL_CONDITIONS_NAME.PANCREATITIS]: {
    icon: "success",
    body: "In general, our fresh meals would be suitable for cats suffering from pancreatitis. Chat with your vet first to see if KatKin would be appropriate for your cat’s specific needs.",
  },
  [MEDICAL_CONDITIONS_NAME.CYSTITIS]: {
    icon: "error",
    body: "The high water content of KatKin can be good for cystitis. However we always recommend discussing this with your vet first before switching diets.",
  },
  [MEDICAL_CONDITIONS_NAME.ARTHRITIS]: {
    icon: "success",
    body: "Our foods are suitable for cats with osteoarthritis. We do not add any glucosamine and chondroitin to our meals, so would advise incorporating a suitable joint supplement to help support your cat’s joints.",
  },
  [MEDICAL_CONDITIONS_NAME.LIVER_DISEASE]: {
    icon: "error",
    body: "Liver disease is a very diverse set of possible conditions, so it's difficult to provide specific recommendations. If your vet has recommended a low-protein diet or a liver or ‘hepatic’ prescription diet, KatKin may not be appropriate for your cat. Speak with your vet first about your cat’s circumstances and whether KatKin would be suitable. We'd be very happy for your cat to join the Club based on your vet’s guidance.",
  },

  [MEDICAL_CONDITIONS_NAME.EPILEPSY]: {
    icon: "success",
    body: "Alongside appropriate medical management, KatKin recipes are suitable for cats with epilepsy. All our KatKin recipes are highly digestible and made with only fresh, quality ingredients – and no nasties.",
  },
  [MEDICAL_CONDITIONS_NAME.CANCER]: {
    icon: "success",
    body: "Alongside veterinary care, KatKin recipes are suitable for cats with cancer. All our KatKin recipes are highly digestible and made with only fresh, quality ingredients – and no nasties.",
  },
  [MEDICAL_CONDITIONS_NAME.FELINE_ASTHMAS]: {
    icon: "success",
    body: "Feline asthma is rarely triggered by diet, and therefore all our foods would be suitable. Environmental allergens are commonly associated with triggering asthma, so reducing your cat’s exposure to these irritants may help to reduce your cat's symptoms.",
  },
};

export const CAT_LIFE_STAGE_TO_AGE_TYPE_MAP: Record<CatLifeStage, AgeType> = {
  [CatLifeStage.ADULT]: "adult",
  [CatLifeStage.KITTEN]: "kitten",
  [CatLifeStage.SENIOR]: "senior",
};
